import { decodeJwtPayload } from '../auth/jwtPayload';

function pickString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function profileRecordFromUnknown(data: unknown): Record<string, unknown> | null {
  if (!isPlainObject(data)) {
    return null;
  }
  if (
    pickString(
      data.first_name,
      data.last_name,
      data.email,
      data.birth_date,
      data.phone_number,
      data.phone,
    )
  ) {
    return data;
  }
  for (const key of ['data', 'user', 'profile', 'client', 'result', 'private_client'] as const) {
    const nested = data[key];
    if (isPlainObject(nested)) {
      return nested;
    }
  }
  return data;
}

function firstNameFromRecord(record: Record<string, unknown> | null | undefined): string | null {
  return record ? pickString(record.first_name, record.firstName) : null;
}

function lastNameFromRecord(record: Record<string, unknown> | null | undefined): string | null {
  return record ? pickString(record.last_name, record.lastName) : null;
}

/** Backend lite-register default last name. */
export function isLiteRegisterPlaceholderLastName(lastName: string | null | undefined): boolean {
  return lastName?.trim().toLowerCase() === 'user';
}

/** Synthetic inbox for phone-only lite-register (`{hash}@phone.offizz.ai`). */
export function isPhoneLiteRegisterSyntheticEmail(email: string | null | undefined): boolean {
  const value = email?.trim().toLowerCase();
  return Boolean(value?.endsWith('@phone.offizz.ai'));
}

/** Backend phone lite-register uses first_name "Phone" with synthetic @phone.offizz.ai email. */
export function isLiteRegisterPlaceholderPhoneFirstName(
  firstName: string | null | undefined,
  email: string | null | undefined,
): boolean {
  return firstName?.trim().toLowerCase() === 'phone' && isPhoneLiteRegisterSyntheticEmail(email);
}

/** Backend lite-register default date of birth (auto-provision / social register). */
export function isLiteRegisterPlaceholderBirthDate(dateOfBirth: string | null | undefined): boolean {
  const value = dateOfBirth?.trim();
  if (!value) {
    return false;
  }
  return value.startsWith('1990-01-01');
}

/**
 * Backend social/Apple auto-register default phone (not a real user number).
 * Digits-only match so formatting variants still count.
 */
export function isLiteRegisterPlaceholderPhoneNumber(phone: string | null | undefined): boolean {
  const digits = phone?.replace(/\D/g, '') ?? '';
  if (!digits) {
    return false;
  }
  // 052-253-0961 / 0522530961 / +972522530961
  return digits === '0522530961' || digits === '972522530961' || digits === '522530961';
}

/** First name copied from the email local-part during lite-register (with other lite signals). */
export function isLiteRegisterPlaceholderFirstName(
  firstName: string | null | undefined,
  email: string | null | undefined,
  context?: { lastName?: string | null; birthDate?: string | null },
): boolean {
  const first = firstName?.trim();
  const mail = email?.trim();
  if (!first || !mail || !mail.includes('@')) {
    return false;
  }
  const localPart = mail.split('@')[0]?.trim().toLowerCase();
  if (!localPart || first.toLowerCase() !== localPart) {
    return false;
  }
  return isLiteRegisterPlaceholderLastName(context?.lastName);
}

export function nullifyLiteRegisterPlaceholderFirstName(
  firstName: string | null | undefined,
  email: string | null | undefined,
  context?: { lastName?: string | null; birthDate?: string | null },
): string | null {
  if (!firstName?.trim()) {
    return null;
  }
  if (isLiteRegisterPlaceholderPhoneFirstName(firstName, email)) {
    return null;
  }
  return isLiteRegisterPlaceholderFirstName(firstName, email, context) ? null : firstName.trim();
}

export function nullifyLiteRegisterPlaceholderLastName(
  lastName: string | null | undefined,
): string | null {
  if (!lastName?.trim()) {
    return null;
  }
  return isLiteRegisterPlaceholderLastName(lastName) ? null : lastName.trim();
}

export function nullifyLiteRegisterPlaceholderBirthDate(
  dateOfBirth: string | null | undefined,
  _lastName?: string | null,
): string | null {
  if (!dateOfBirth?.trim()) {
    return null;
  }
  // Always hide the auto-provision DOB — Apple/Google users get real names but still receive 1990-01-01.
  return isLiteRegisterPlaceholderBirthDate(dateOfBirth) ? null : dateOfBirth.trim();
}

export function nullifyLiteRegisterPlaceholderPhoneNumber(
  phone: string | null | undefined,
): string | null {
  if (!phone?.trim()) {
    return null;
  }
  return isLiteRegisterPlaceholderPhoneNumber(phone) ? null : phone.trim();
}

/** True when GET /profile (or login/JWT hints) still has auto-provisioned placeholder values. */
export function profileRecordUsesLiteRegisterPlaceholders(
  record: Record<string, unknown> | null | undefined,
  emailHint?: string | null,
): boolean {
  if (!record) {
    return false;
  }
  const email = pickString(record.email, record.Email) ?? emailHint ?? null;
  const lastName = lastNameFromRecord(record);
  const firstName = firstNameFromRecord(record);
  const liteLast = isLiteRegisterPlaceholderLastName(lastName);
  const placeholderFirst =
    isLiteRegisterPlaceholderFirstName(firstName, email, { lastName }) ||
    isLiteRegisterPlaceholderPhoneFirstName(firstName, email);
  return placeholderFirst || (liteLast && !firstName);
}

/** Strip lite-register placeholders from a merged profile snapshot (booking checks). */
export function stripLiteRegisterPlaceholdersFromSnapshot(
  snapshot: Record<string, unknown>,
  emailHint?: string | null,
): Record<string, unknown> {
  const email = pickString(snapshot.email, snapshot.Email, emailHint);
  const out = { ...snapshot };
  const first = pickString(out.first_name, out.firstName);
  const last = pickString(out.last_name, out.lastName);
  const dob = pickString(out.birth_date, out.date_of_birth, out.dob, out.birthDate);
  const placeholderContext = { lastName: last };
  const placeholderFirst =
    isLiteRegisterPlaceholderFirstName(first, email, placeholderContext) ||
    isLiteRegisterPlaceholderPhoneFirstName(first, email);
  const firstSanitized = nullifyLiteRegisterPlaceholderFirstName(first, email, placeholderContext);
  const lastSanitized = placeholderFirst
    ? nullifyLiteRegisterPlaceholderLastName(last)
    : last?.trim() || null;
  const dobSanitized = isLiteRegisterPlaceholderBirthDate(dob) ? null : dob?.trim() || null;

  if (firstSanitized) {
    out.first_name = firstSanitized;
  } else {
    delete out.first_name;
    delete out.firstName;
  }
  if (lastSanitized) {
    out.last_name = lastSanitized;
  } else {
    delete out.last_name;
    delete out.lastName;
  }
  if (dobSanitized) {
    out.birth_date = dobSanitized;
  } else {
    delete out.birth_date;
    delete out.date_of_birth;
    delete out.dob;
    delete out.birthDate;
  }

  if (isPhoneLiteRegisterSyntheticEmail(email)) {
    delete out.email;
    delete out.Email;
  }

  const phoneValue = pickString(
    out.phone_number,
    out.phone,
    out.phoneNumber,
    out.mobile,
    out.cell_phone,
  );
  if (isLiteRegisterPlaceholderPhoneNumber(phoneValue)) {
    delete out.phone_number;
    delete out.phone;
    delete out.phoneNumber;
    delete out.mobile;
    delete out.cell_phone;
  } else if (profileRecordUsesLiteRegisterPlaceholders(snapshot, email)) {
    if (!isPhoneLiteRegisterSyntheticEmail(email)) {
      delete out.phone_number;
      delete out.phone;
      delete out.phoneNumber;
      delete out.mobile;
      delete out.cell_phone;
    }
  }

  return out;
}

/** Detect lite-register placeholders from OTP/login JSON and JWT claims before caching contact hints. */
export function loginResponseUsesLiteRegisterPlaceholders(
  body: unknown,
  accessToken: string | null | undefined,
  loginIdentifier: string | null | undefined,
): boolean {
  const fromApi = profileRecordFromUnknown(body);
  if (profileRecordUsesLiteRegisterPlaceholders(fromApi, loginIdentifier)) {
    return true;
  }

  const payload = accessToken ? decodeJwtPayload(accessToken) : null;
  if (!payload) {
    return false;
  }

  const email = pickString(
    fromApi?.email,
    payload.email,
    payload.preferred_username,
    payload.unique_name,
    loginIdentifier,
  );
  const synthetic: Record<string, unknown> = {
    email,
    first_name: pickString(
      fromApi?.first_name,
      fromApi?.firstName,
      payload.given_name,
      payload.first_name,
    ),
    last_name: pickString(
      fromApi?.last_name,
      fromApi?.lastName,
      payload.family_name,
      payload.last_name,
    ),
    birth_date: pickString(fromApi?.birth_date, fromApi?.date_of_birth, payload.birth_date),
  };
  return profileRecordUsesLiteRegisterPlaceholders(synthetic, email);
}